import React, { useCallback } from 'react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import type { Query } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'

import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

import CohostReviewTable from './CohostReviewTable'
import type { HostToastInfo } from './types'

const CohostReviewModal: React.FC<{
  showModal: boolean
  cohosts: Query[]
  numCohostRequests: number
  isSession: boolean
  setShowModal: (show: boolean) => void
}> = ({ showModal, cohosts, numCohostRequests, isSession, setShowModal }) => {
  const toast = useToaster()
  const [rejectHostInfo, setRejectHostInfo] = React.useState<HostToastInfo | null>(null)

  const [handleApproveCohost, isApprovingCohost] = useAPIWrapper(
    async (hostInfo: HostToastInfo) => {
      if (hostInfo == null) {
        return
      }
      if (isSession) {
        await RPCRefresh.approveSessionHost({
          sessionHostId: hostInfo.hostId,
        })
      } else {
        await RPCRefresh.approveSeriesHost({
          seriesHostId: hostInfo.hostId,
        })
      }
      toast({
        title: `Approved ${hostInfo.name}`,
        status: 'success',
      })
    },
    [toast, isSession],
    'Approve Co-host'
  )

  const rejectionFormInputs: FormInputType[] = React.useMemo(
    () => [
      {
        label: `Please try to explain to ${rejectHostInfo?.name} why their co-host request is being rejected`,
        type: 'textarea',
        name: 'reason',
        placeholder: `Your message here will be sent to ${rejectHostInfo?.name} via email, along with their rejection. Remember to be nice :)`,
        required: false,
        size: 6,
      },
    ],
    [rejectHostInfo]
  )

  const handleReject = useCallback(
    async (values: FormValues) => {
      if (rejectHostInfo == null) {
        return
      }
      if (isSession) {
        await RPCRefresh.removeSessionHost({
          sessionHostId: rejectHostInfo.hostId,
          deletedMessage: `${values.reason}`,
        })
      } else {
        await RPCRefresh.removeSeriesHost({
          seriesHostId: rejectHostInfo.hostId,
          deletedMessage: `${values.reason}`,
        })
      }
      toast({
        title: `Rejected ${rejectHostInfo.name}`,
        status: 'success',
      })
      setRejectHostInfo(null)
    },
    [toast, rejectHostInfo, isSession]
  )
  return (
    <>
      <ModalV3
        size="xl"
        open={showModal}
        onClose={() => setShowModal(false)}
        body={() => (
          <CohostReviewTable
            numCohostRequests={numCohostRequests}
            cohosts={cohosts}
            isSession={isSession}
            onApproveCohost={handleApproveCohost}
            isApprovingCohost={isApprovingCohost}
            setRejectHostInfo={setRejectHostInfo}
            setShowModal={setShowModal}
          />
        )}
      />
      <ModalV3
        title="Reject Co-host"
        size="xl"
        open={rejectHostInfo != null}
        onClose={() => {
          setRejectHostInfo(null)
        }}
        body={() => (
          <Form name="CohostReject" blocks={rejectionFormInputs} onSubmit={handleReject} />
        )}
      />
    </>
  )
}

export default CohostReviewModal
